import type { I18N } from '@vista-digital/i18n'
import type { Film, MonetaryValue, OrderTicketLine, Site } from '@vista-digital/ocapi-types/v1'
import {
    getFilmForShowtime,
    getItemById,
    getOrderItems,
    getOrderTickets,
    getShowtimeById,
    getSiteById,
} from '@vista-digital/occ-domain'
import type { OrderItemLineSelection } from '@vista-digital/occ-domain/src/lib/domains/order/types/order-item-line-selection'
import { CurzonEcommerceDataLayer, FilmOrderItem } from '@/common/services/models-gtm'
import { gtm } from '../gtm.service'

export const mapTicketItem = (props: {
    i18n: I18N
    ticket: OrderTicketLine
    film: Film
    site: Site
    includeItemId?: boolean
}) => {
    const { i18n, ticket, film, site, includeItemId = false } = props
    return new FilmOrderItem({
        item_id: includeItemId ? ticket.id : undefined,
        item_name: i18n.translatable(film.title), // "No Time To Die"
        location_id: i18n.translatable(site.name), // "Victoria"
        price: ticket.price.valueIncludingTax, // 10.00 - do not wrap in quotation marks as it's an integer
        item_category: 'Film',
        quantity: 1, // Also an integer
    })
}

export const mapTicketItems = (props: { i18n: I18N; film: Film; site: Site; includeItemId?: boolean }) => {
    const selectedTickets = getOrderTickets()
    return selectedTickets.map(x => {
        return mapTicketItem({
            ...props,
            ticket: x,
        })
    })
}

export const mapOrderItems = (props: {
    i18n: I18N
    site: Site
    includeItemId?: boolean
    getItemNameBy: (item: OrderItemLineSelection) => string
}) => {
    const { i18n, site, includeItemId } = props
    return getOrderItems().map(x => {
        return new FilmOrderItem({
            item_id: includeItemId ? x.itemId : undefined,
            item_name: props.getItemNameBy(x), // "No Time To Die"
            location_id: i18n.translatable(site.name),
            price: x.price.valueIncludingTax,
            item_category: 'Item',
            quantity: 1,
        })
    })
}

export const getShowtimeData = (showtimeId: string) => {
    const film = getFilmForShowtime(showtimeId)
    const showtime = getShowtimeById(showtimeId)
    const site = getSiteById(showtime?.siteId || '')
    return { film, showtime, site }
}

export class TicketingGtmEvents {
    public static addToCart(i18n: I18N, showtimeId: string) {
        const { film, showtime, site } = getShowtimeData(showtimeId)
        if (!film || !showtime || !site) return
        const ticketItems = mapTicketItems({ i18n, film, site })
        void gtm.sendEcommerceDataLayer(
            new CurzonEcommerceDataLayer('add_to_cart', {
                items: ticketItems,
            })
        )
    }
    public static async beginCheckout(i18n: I18N, showtimeId: string) {
        const { film, showtime, site } = getShowtimeData(showtimeId)
        if (!film || !showtime || !site) return
        const ticketItems = mapTicketItems({ i18n, film, site })
        const getItemNameBy = (x: OrderItemLineSelection) => {
            const item = getItemById(x.itemId)
            return item ? i18n.translatable(item.name) : ''
        }
        const orderItems = mapOrderItems({ i18n, site, getItemNameBy })

        await gtm.sendEcommerceDataLayer(
            new CurzonEcommerceDataLayer('begin_checkout', {
                items: [...ticketItems, ...orderItems],
            })
        )
    }

    public static async purchase(data: {
        i18n: I18N
        orderId: string
        orderTotal: MonetaryValue
        items: FilmOrderItem[]
    }) {
        const { orderTotal, orderId, items } = data
        await gtm.sendEcommerceDataLayer(
            new CurzonEcommerceDataLayer('purchase', {
                transaction_id: orderId,
                vista_transaction_id: orderId,
                value: orderTotal.valueIncludingTax,
                tax: orderTotal.tax,
                currency: 'GBP',
                items: items,
            })
        )
    }
}
